import { api } from './api';

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;

export type PushState =
  | { kind: 'unsupported' }
  | { kind: 'denied' }
  | { kind: 'idle' }              // permission default, no subscription
  | { kind: 'subscribed'; endpoint: string };

function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getPushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return { kind: 'unsupported' };
  }
  if (Notification.permission === 'denied') return { kind: 'denied' };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) return { kind: 'subscribed', endpoint: sub.endpoint };
  return { kind: 'idle' };
}

export async function enablePush(token: string): Promise<PushState> {
  if (!VAPID_PUBLIC) throw new Error('VAPID_PUBLIC_KEY not configured');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { kind: 'denied' };

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC).buffer as ArrayBuffer,
    });
  }
  const subJson = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.pushSubscribe(
    { endpoint: subJson.endpoint, keys: subJson.keys, ua: navigator.userAgent },
    token,
  );
  return { kind: 'subscribed', endpoint: sub.endpoint };
}

export async function disablePush(token: string): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.pushUnsubscribe(sub.endpoint, token); } catch { /* noop */ }
    await sub.unsubscribe();
  }
}
